import config from "../config";

const CHUNK_ERROR_PATTERN =
  /chunkloaderror|loading (?:css )?chunk|failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|unable to preload css/i;
const URL_PATTERN = /https?:\/\/[^\s"'<>]+/g;
const MAX_MESSAGE_LENGTH = 500;
const SESSION_DEDUPE_PREFIX = "advisorspace:client-diagnostic:";

interface ClientDiagnostic {
  asset: string | null;
  buildVersion: string | null;
  category: "asset_load_error" | "chunk_load_error";
  errorName: string;
  message: string;
  page: string;
  source: "error" | "resource" | "unhandledrejection" | "vite:preloadError";
}

interface DiagnosticInput {
  asset?: string | null;
  category: ClientDiagnostic["category"];
  error: Error;
  source: ClientDiagnostic["source"];
}

interface InstallOptions {
  report?: (diagnostic: ClientDiagnostic) => unknown;
}

declare global {
  interface Window {
    FS?: {
      event?: (name: string, properties: ClientDiagnostic) => void;
    };
  }
}

function sanitizeURL(value: unknown): string | null {
  if (!value) {
    return null;
  }
  try {
    const url = new URL(String(value), window.location.origin);
    return url.origin === window.location.origin ? url.pathname : `${url.origin}${url.pathname}`;
  } catch (_error) {
    return null;
  }
}

function isApplicationAsset(value: unknown): boolean {
  try {
    const url = new URL(String(value), window.location.origin);
    return url.origin === window.location.origin && /\.(?:css|js)$/i.test(url.pathname);
  } catch (_error) {
    return false;
  }
}

function sanitizeMessage(value: unknown): string {
  const message = String(value || "Unknown chunk-loading failure").replace(
    URL_PATTERN,
    (url) => sanitizeURL(url) || url,
  );
  return message.slice(0, MAX_MESSAGE_LENGTH);
}

function getError(value: unknown): Error {
  if (value instanceof Error) {
    return value;
  }
  if (value && typeof value === "object") {
    const candidate = value as { detail?: unknown; message?: unknown; reason?: unknown };
    if (candidate.reason instanceof Error) {
      return candidate.reason;
    }
    if (candidate.detail instanceof Error) {
      return candidate.detail;
    }
    return new Error(
      String(candidate.message || candidate.reason || candidate.detail || "Unknown chunk-loading failure"),
    );
  }
  return new Error(String(value || "Unknown chunk-loading failure"));
}

function isChunkError(error: Error): boolean {
  return error.name === "ChunkLoadError" || CHUNK_ERROR_PATTERN.test(error.message);
}

function getBuildVersion(): string | null {
  try {
    return localStorage.getItem("version");
  } catch (_error) {
    return null;
  }
}

function createDiagnostic({ asset = null, category, error, source }: DiagnosticInput): ClientDiagnostic {
  return {
    asset: sanitizeURL(asset),
    buildVersion: getBuildVersion(),
    category,
    errorName: error.name || "Error",
    message: sanitizeMessage(error.message),
    page: window.location.pathname,
    source,
  };
}

function diagnosticSessionKey(signature: string): string {
  let hash = 2_166_136_261;
  for (let index = 0; index < signature.length; index += 1) {
    hash ^= signature.charCodeAt(index);
    hash = Math.imul(hash, 16_777_619);
  }
  return `${SESSION_DEDUPE_PREFIX}${signature.length}-${(hash >>> 0).toString(36)}`;
}

function wasReportedThisSession(signature: string): boolean {
  try {
    const key = diagnosticSessionKey(signature);
    if (sessionStorage.getItem(key)) {
      return true;
    }
    sessionStorage.setItem(key, "1");
  } catch (_error) {
    // Private browsing and restrictive policies may make storage unavailable.
  }
  return false;
}

function reportClientDiagnostic(diagnostic: ClientDiagnostic): void {
  if (config.env === "development") {
    return;
  }

  try {
    if (typeof window.FS?.event === "function") {
      window.FS.event("Frontend Runtime Diagnostic", diagnostic);
    }
  } catch (_error) {
    // A monitoring integration must never disrupt the application.
  }
}

export default function installClientDiagnostics({ report = reportClientDiagnostic }: InstallOptions = {}): () => void {
  const reported = new Set();
  const reportOnce = (diagnostic: ClientDiagnostic) => {
    const signature = [
      diagnostic.category,
      diagnostic.source,
      diagnostic.asset,
      diagnostic.errorName,
      diagnostic.message,
      diagnostic.page,
      diagnostic.buildVersion,
    ].join("|");
    if (reported.has(signature) || wasReportedThisSession(signature)) {
      return;
    }
    reported.add(signature);
    report(diagnostic);
  };

  const handleError = (event: ErrorEvent | Event) => {
    const resource = event.target;
    if (resource instanceof HTMLScriptElement || resource instanceof HTMLLinkElement) {
      const asset = resource instanceof HTMLScriptElement ? resource.src : resource.href;
      if (!isApplicationAsset(asset)) {
        return;
      }
      const error = new Error("Failed to load an application asset.");
      error.name = "ResourceLoadError";
      reportOnce(
        createDiagnostic({
          asset,
          category: "asset_load_error",
          error,
          source: "resource",
        }),
      );
      return;
    }

    const error = getError(event instanceof ErrorEvent ? event.error || event.message : event);
    if (isChunkError(error)) {
      reportOnce(createDiagnostic({ category: "chunk_load_error", error, source: "error" }));
    }
  };

  const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
    const error = getError(event.reason);
    if (isChunkError(error)) {
      reportOnce(createDiagnostic({ category: "chunk_load_error", error, source: "unhandledrejection" }));
    }
  };

  const handleVitePreloadError = (event: VitePreloadErrorEvent) => {
    const error = getError(event.payload);
    reportOnce(createDiagnostic({ category: "chunk_load_error", error, source: "vite:preloadError" }));
  };

  window.addEventListener("error", handleError, true);
  window.addEventListener("unhandledrejection", handleUnhandledRejection);
  window.addEventListener("vite:preloadError", handleVitePreloadError);

  return () => {
    window.removeEventListener("error", handleError, true);
    window.removeEventListener("unhandledrejection", handleUnhandledRejection);
    window.removeEventListener("vite:preloadError", handleVitePreloadError);
  };
}
